import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import { PlusIcon, FolderIcon, ChevronDownIcon } from '../../../model/icons';
import SimpleNameModal from './SimpleNameModal';
import TemplateCategoryModal from './TemplateCategoryModal';
import TemplateModal from './TemplateModal';
import TemplateCard from './TemplateCard';
import ChallengeTemplateModal from './challenges/ChallengeTemplateModal';
import { CHALLENGE_HOME_NAME } from './challenges/challengeUtils';

// Панель «Шаблоны»: паки (обычные и с челленджами).
export default function TemplatesPanel() {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { data: packs = [], refetch } = useApiQuery(
        ['neighbor', 'template-packs'],
        '/api/neighbor/admin/task-template-packs/list-all',
        { refetchOnMount: 'always' }
    );
    const [createOpen, setCreateOpen] = useState(false); // 'normal' | 'challenge' | false

    const createPack = async (name, challenge) => {
        show();
        try {
            const res = await apiClient.post('/api/neighbor/admin/task-template-packs/create', { name });
            if (challenge) await ensureChallengeHome(res.data.id);
            enqueueSnackbar(challenge ? 'Пак с челленджами создан' : 'Пак создан', { variant: 'success' });
            refetch();
            setCreateOpen(false);
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <div className="bt-panel">
            <div className="sp-secthead">
                <h2>Паки шаблонов</h2>
                <div className="sp-headacts">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreateOpen('challenge')}><span className="ic"><PlusIcon /></span>Пак с челленджами</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen('normal')}><span className="ic"><PlusIcon /></span>Создать пак</button>
                </div>
            </div>
            {packs.length > 0 ? (
                packs.map(pack => <PackAccordion key={pack.id} pack={pack} refetch={refetch} />)
            ) : <div className="bt-empty">Нет созданных паков шаблонов</div>}

            {createOpen && (
                <SimpleNameModal
                    kicker={createOpen === 'challenge' ? 'Пак с челленджами' : 'Пак шаблонов'}
                    title={createOpen === 'challenge' ? 'Создание пака с челленджами' : 'Создание пака'}
                    label="Название пака"
                    onSave={(name) => createPack(name, createOpen === 'challenge')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
        </div>
    );
}

function PackAccordion({ pack, refetch }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [createCatOpen, setCreateCatOpen] = useState(false);

    // Грузим сразу (не по open), чтобы знать тип пака для бейджа и тела.
    const { data: categories = [], refetch: refetchCats } = useApiQuery(
        ['neighbor', 'template-categories', pack.id],
        `/api/neighbor/admin/task-template-categories/list-by-pack?packId=${pack.id}`,
        { refetchOnMount: 'always' }
    );
    const isChallengePack = categories.some(c => c.name === CHALLENGE_HOME_NAME);
    const visibleCategories = categories.filter(c => c.name !== CHALLENGE_HOME_NAME).sort((a, b) => a.name.localeCompare(b.name));

    const savePack = async (name) => {
        show();
        try {
            await apiClient.patch(`/api/neighbor/admin/task-template-packs/update?id=${pack.id}`, { name });
            enqueueSnackbar('Пак обновлён', { variant: 'success' });
            refetch();
            setEditOpen(false);
        } catch (e) { enqueueSnackbar('Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };
    const delPack = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-template-packs/delete?id=${pack.id}`);
            enqueueSnackbar('Пак удалён', { variant: 'success' });
            refetch();
            setEditOpen(false);
        } catch (e) { enqueueSnackbar('Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <div className={`sp-acc${open ? ' open' : ''}`}>
            <div className="sp-acc-head" role="button" tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(!open); }}>
                <div className="nm">
                    <span className="sp-folder"><FolderIcon /></span>{pack.name}
                    {isChallengePack && <span className="sp-mini">челленджи</span>}
                </div>
                <div className="sp-acc-acts">
                    <button type="button" className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setEditOpen(true); }}>Редактировать</button>
                    <span className="sp-acc-chev"><ChevronDownIcon /></span>
                </div>
            </div>
            {open && (
                <div className="sp-acc-body">
                    {isChallengePack ? (
                        <ChallengePackBody packId={pack.id} />
                    ) : (
                        <>
                            <div className="sp-secthead">
                                <h3>Категории</h3>
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateCatOpen(true)}><span className="ic"><PlusIcon /></span>Добавить категорию</button>
                            </div>
                            {visibleCategories.length > 0 ? (
                                visibleCategories.map(cat => (
                                    <CategoryAccordion key={cat.id} category={cat} refetch={refetchCats} />
                                ))
                            ) : <div className="bt-empty">Нет категорий в этом паке</div>}
                        </>
                    )}
                </div>
            )}
            {editOpen && (
                <SimpleNameModal kicker="Пак шаблонов" title="Редактирование пака" label="Название" initial={pack.name} onSave={savePack} onDelete={delPack} onClose={() => setEditOpen(false)} />
            )}
            {createCatOpen && (
                <TemplateCategoryModal packId={pack.id} onSuccess={refetchCats} onClose={() => setCreateCatOpen(false)} />
            )}
        </div>
    );
}

PackAccordion.propTypes = { pack: PropTypes.object.isRequired, refetch: PropTypes.func.isRequired };

function CategoryAccordion({ category, refetch }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selSub, setSelSub] = useState(null);
    const [subModal, setSubModal] = useState(null);
    const [tmplModal, setTmplModal] = useState(null);

    const { data: subcategories = [], refetch: refetchSubs } = useApiQuery(
        ['neighbor', 'template-subcategories', category.id],
        `/api/neighbor/admin/task-template-subcategories/list-by-category?categoryId=${category.id}`,
        { enabled: open }
    );
    const { data: templates = [], refetch: refetchTemplates } = useApiQuery(
        ['neighbor', 'templates', category.id],
        `/api/neighbor/admin/task-templates/list-by-category?categoryId=${category.id}`,
        { enabled: open }
    );

    const saveSub = async (name) => {
        show();
        try {
            if (subModal?.subcategory) {
                await apiClient.patch(`/api/neighbor/admin/task-template-subcategories/update?id=${subModal.subcategory.id}`, { name });
            } else {
                await apiClient.post('/api/neighbor/admin/task-template-subcategories/create', { categoryId: category.id, name });
            }
            enqueueSnackbar('Сохранено', { variant: 'success' });
            refetchSubs();
            setSubModal(null);
        } catch (e) { enqueueSnackbar('Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };
    const delSub = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-template-subcategories/delete?id=${subModal.subcategory.id}`);
            enqueueSnackbar('Подкатегория удалена', { variant: 'success' });
            if (selSub?.id === subModal.subcategory.id) setSelSub(null);
            refetchSubs();
            setSubModal(null);
        } catch (e) { enqueueSnackbar('Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };

    // Челленджи вынесены в псевдо-категорию пака — из обычной категории их исключаем.
    const filtered = (selSub ? templates.filter(t => t.subcategoryId === selSub.id) : templates).filter(t => !t.isChallenge);

    return (
        <div className={`sp-acc nested${open ? ' open' : ''}`}>
            <div className="sp-acc-head" role="button" tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(!open); }}>
                <div className="nm">
                    <span className="sp-folder"><FolderIcon /></span>
                    <div>{category.name}<div className="sub">Категория шаблонов</div></div>
                </div>
                <div className="sp-acc-acts">
                    {category.ratioLimit && <span className="sp-mini">Коэф: {category.ratioLimit}</span>}
                    {category.dailyLimit && <span className="sp-mini">В день: {category.dailyLimit}</span>}
                    <button type="button" className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setEditOpen(true); }}>Редактировать</button>
                    <span className="sp-acc-chev"><ChevronDownIcon /></span>
                </div>
            </div>
            {open && (
                <div className="sp-acc-body">
                    <div className="sp-subbar-top">
                        <span className="sp-sublabel">Подкатегории</span>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSubModal({ create: true })}><span className="ic"><PlusIcon /></span>Подкатегория</button>
                    </div>
                    <div className="sp-chips">
                        <span className={`sp-chip${!selSub ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => setSelSub(null)} onKeyDown={(e) => { if (e.key === 'Enter') setSelSub(null); }}>Все</span>
                        {subcategories.map(sub => (
                            <span key={sub.id} className={`sp-chip${selSub?.id === sub.id ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => setSelSub(sub)} onKeyDown={(e) => { if (e.key === 'Enter') setSelSub(sub); }}>
                                {sub.name}
                                <button type="button" className="sp-chipgear" onClick={(e) => { e.stopPropagation(); setSubModal({ subcategory: sub }); }}><PlusIcon /></button>
                            </span>
                        ))}
                    </div>

                    <div className="sp-secthead" style={{ marginTop: 18 }}>
                        <h3>Шаблоны</h3>
                        <button type="button" className="btn btn-primary btn-sm" disabled={subcategories.length === 0} onClick={() => setTmplModal({ create: true })}><span className="ic"><PlusIcon /></span>Добавить шаблон</button>
                    </div>
                    {filtered.length > 0 ? (
                        <div className="sp-tasks">
                            {filtered.map(t => <TemplateCard key={t.id} template={t} subcategories={subcategories} onClick={() => setTmplModal({ template: t })} />)}
                        </div>
                    ) : <div className="bt-empty">Нет шаблонов</div>}
                </div>
            )}

            {editOpen && (
                <TemplateCategoryModal category={category} onSuccess={refetch} onClose={() => setEditOpen(false)} />
            )}
            {subModal && (
                <SimpleNameModal
                    kicker="Подкатегория"
                    title={subModal.subcategory ? 'Редактировать подкатегорию' : 'Создать подкатегорию'}
                    label="Название"
                    initial={subModal.subcategory?.name}
                    onSave={saveSub}
                    onDelete={subModal.subcategory ? delSub : null}
                    onClose={() => setSubModal(null)}
                />
            )}
            {tmplModal && (
                <TemplateModal
                    template={tmplModal.template}
                    categoryId={category.id}
                    subcategories={subcategories}
                    defaultSubcategoryId={selSub?.id}
                    onSuccess={refetchTemplates}
                    onClose={() => setTmplModal(null)}
                />
            )}
        </div>
    );
}

CategoryAccordion.propTypes = { category: PropTypes.object.isRequired, refetch: PropTypes.func.isRequired };

// Дефолтная категория+подкатегория, где физически лежат челленджи пака (скрыты из обычных категорий).
async function ensureChallengeHome(packId) {
    const cats = (await apiClient.get(`/api/neighbor/admin/task-template-categories/list-by-pack?packId=${packId}`)).data || [];
    let cat = cats.find(c => c.name === CHALLENGE_HOME_NAME);
    if (!cat) {
        cat = (await apiClient.post('/api/neighbor/admin/task-template-categories/create', { name: CHALLENGE_HOME_NAME, ratioLimit: null, dailyLimit: null, packId })).data;
    }
    const subs = (await apiClient.get(`/api/neighbor/admin/task-template-subcategories/list-by-category?categoryId=${cat.id}`)).data || [];
    let sub = subs.find(s => s.name === CHALLENGE_HOME_NAME);
    if (!sub) {
        sub = (await apiClient.post('/api/neighbor/admin/task-template-subcategories/create', { categoryId: cat.id, name: CHALLENGE_HOME_NAME })).data;
    }
    return { categoryId: cat.id, subcategoryId: sub.id };
}

// Тело пака-челленджей: плоский список челленджей + создание (без категорий).
function ChallengePackBody({ packId }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [modal, setModal] = useState(null); // { create, categoryId, subcategoryId } | { template }

    const { data: packTemplates = [], refetch } = useApiQuery(
        ['neighbor', 'pack-templates', packId],
        `/api/neighbor/admin/task-templates/list-by-pack?packId=${packId}`,
        { refetchOnMount: 'always' }
    );
    const challenges = packTemplates.filter(t => t.isChallenge);

    const createChallenge = async () => {
        show();
        try {
            const home = await ensureChallengeHome(packId);
            setModal({ create: true, ...home });
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Не удалось подготовить челленджи', { variant: 'error' }); }
        finally { hide(); }
    };

    // Легаси-шаблоны без скрытой категории/подкатегории — дорезолвим дом, чтобы update не ушёл с null.
    const openTemplate = async (template) => {
        if (template.categoryId && template.subcategoryId) { setModal({ template }); return; }
        show();
        try {
            const home = await ensureChallengeHome(packId);
            setModal({ template, ...home });
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Не удалось подготовить челленджи', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <>
            <div className="sp-secthead">
                <h3>Челленджи</h3>
                <button type="button" className="btn btn-primary btn-sm" onClick={createChallenge}>
                    <span className="ic"><PlusIcon /></span>Создать челлендж
                </button>
            </div>
            {challenges.length > 0 ? (
                <div className="sp-tasks">
                    {challenges.map(t => <TemplateCard key={t.id} template={t} subcategories={[]} onClick={() => openTemplate(t)} />)}
                </div>
            ) : <div className="bt-empty">Нет челленджей — нажмите «Создать челлендж»</div>}

            {modal && (
                <ChallengeTemplateModal
                    template={modal.template}
                    categoryId={modal.template?.categoryId ?? modal.categoryId}
                    subcategoryId={modal.template?.subcategoryId ?? modal.subcategoryId}
                    onSaved={refetch}
                    onClose={() => setModal(null)}
                />
            )}
        </>
    );
}

ChallengePackBody.propTypes = { packId: PropTypes.number.isRequired };
